<template>
    <div class="RadioContainer">
        <FieldLabel
            :label="label"
            :label-tooltip="labelTooltip"
        />
        <div class="RadioContainer__option-container">
            <label
                v-for="option in options"
                :key="JSON.stringify(option.value)"
                class="RadioContainer__option"
                @click="selectOption(option.value)"
            >
                <input
                    :checked="isValueEqual(option.value)"
                    type="radio"
                    :value="option.value"
                    class="RadioContainer__option-button"
                >
                <StreamlineIcon
                    v-if="hasIcon(option)"
                    class="RadioContainer__option-icon"
                    :icon="option.icon"
                    stroke="black"
                    :size="12"
                />
                {{ option.label }}
            </label>
        </div>
    </div>
</template>
<script>
import isEqual from 'lodash/isEqual';
import has from 'lodash/has';
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import RPTooltip from 'components/Basic/Misc/RPTooltip.vue';
import FieldLabel from 'components/Basic/Misc/FieldLabel';

export default {
    name: 'RPRadioContainer',
    components: {
        StreamlineIcon,
        FieldLabel
    },
    props: {
        /**
         * @model
         */
        value: {
            type: [String, Number, Boolean, Object],
            required: true,
        },
        /**
         * An array of objects with `value`, `label`, and (optionally) `icon` properties
         */
        options: {
            type: Array,
            required: true,
        },
        label: {
            type: String,
            default: ''
        },
        labelTooltip: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        hasIcon(opt){
            return has(opt, 'icon');
        },
        isValueEqual(val){
            return isEqual(val, this.value);
        },
        selectOption(val){
            /**
             * Used for v-model
             */
            if(!this.disabled){
                this.$emit('input', val);
            }
        }
    }
};
</script>
<style lang="scss">
    .RadioContainer {
        &__option-container{
            display: flex;
            flex-direction: column;
            flex-wrap: nowrap;
        }
        &__option {
            cursor: pointer;
            color: $text-grey;
            display: flex;
            align-items: center;
            margin-right: 0.15rem;
            height: 1rem;
            white-space: nowrap;
            &:not(:last-child){ margin-bottom: 0.75rem; }
        }
        &__option-icon {
            margin-right: 0.25rem;
        }
        &__option-button {
            cursor: pointer;
            height: 1rem;
            margin: auto 0.5rem auto 0;
        }
    }
</style>

<docs>
Simple boolean toggle:
```vue
<RPRadioContainer
    v-model="myValBoolean"
    :options="[
        {label: 'Yes', value: true},
        {label: 'No', value: false},
    ]"
>
</RPRadioContainer>
<br>
<pre>{{ {"v-model": myValBoolean} }}</pre>
```

Choosing from a list:
```vue
import { FruitApple, FruitBanana } from "@reviewpush/streamline-regular/lib/food-drinks/Fruits";

<RPRadioContainer
    v-model="myValString"
    label="Choose a fruit"
    labelTooltip="Don't pick the wrong fruit!!!"
    :options="[
        {label: 'Apple', value: 'apple', icon: FruitApple},
        {label: 'Banana', value: 'banana', icon: FruitBanana},
        {label: 'Mango', value: 'mango'}
    ]"
/>
<br>
<pre>{{ {"v-model": myValString} }}</pre>
```
</docs>
